import { useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Button, Row, Col, Card, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { useCreateReturnRequestMutation } from '../slices/returnsApiSlice';
import { useGetOrderDetailsQuery } from '../slices/ordersApiSlice';
import Loader from '../Components/Loader';
import BackButton from "../Components/BackButton";


function isEomOrder(order) {
  const paymentMethod = String(order?.paymentMethod || '').toLowerCase();
  return (
    paymentMethod.includes('end of month') ||
    paymentMethod.includes('eom') ||
    paymentMethod.includes('monthly') ||
    order?.user?.endOfMonthPaymentEnabled === true
  );
}

function isOrderCompletedEnoughForEomReturn(order) {
  const status = String(order?.status || '').toLowerCase();
  return (
    order?.isDelivered === true ||
    !!order?.deliveredAt ||
    !!order?.collectedAt ||
    status === 'delivered' ||
    status === 'collected' ||
    status === 'completed'
  );
}

const REASONS = [
  { value: 'wrong_item', label: 'Wrong item ordered' },
  { value: 'not_needed', label: 'No longer needed' },
  { value: 'damaged', label: 'Damaged in transit' },
  { value: 'defective', label: 'Defective / not working' },
  { value: 'other', label: 'Other' },
];

export default function ReturnRequestScreen() {
  const { orderId = '' } = useParams();
  const navigate = useNavigate();
  const { userInfo } = useSelector((s) => s.auth);

  const [name, setName] = useState(userInfo?.name || '');
  const [email, setEmail] = useState(userInfo?.email || '');
  const [phone, setPhone] = useState('');
  const [orderNumber, setOrderNumber] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [reason, setReason] = useState(REASONS[0].value);
  const [details, setDetails] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const [createRR, { isLoading }] = useCreateReturnRequestMutation();
  const { data: orderDetails } = useGetOrderDetailsQuery(orderId, {
    skip: !orderId || !userInfo,
  });
  const eomOrderNotReady =
    orderId &&
    orderDetails &&
    isEomOrder(orderDetails) &&
    !isOrderCompletedEnoughForEomReturn(orderDetails);

  // lightweight email + phone helpers
  const isValidEmail = (e) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(e || '').trim());
  const normalizePhone = (p) => String(p || '').replace(/[^\d+]/g, '');

  const canSubmit = useMemo(
    () =>
      !!name.trim() &&
      isValidEmail(email) &&
      !!reason &&
      // allow either logged-in orderId flow OR guest order number flow
      (!!orderId || !!orderNumber.trim()) &&
      !eomOrderNotReady,
    [orderId, orderNumber, name, email, reason, eomOrderNotReady]
  )

  const submit = async (e) => {
    e.preventDefault();
    setSubmitted(false);
    if (!email || !name) {
      toast.error('Name and Email are required');
       return;
     }
    // Require either a route orderId (logged-in) OR an Order/Invoice number (guest)
    if (!orderId && !orderNumber.trim()) {
      toast.error('Please enter your Order/Invoice number');
      return;
    }
    if (eomOrderNotReady) {
      toast.error('This EOM order is eligible for return after delivery or pickup is completed.');
      return;
    }
    // Normalize postal (optional)
    const postal = postalCode ? postalCode.replace(/\s+/g, '').toUpperCase() : undefined;
    if (!canSubmit) return;
    try {
      await createRR({
        // linkage (both supported so backend can accept either)
        orderId: orderId || undefined,
        orderNumber: orderNumber.trim() || undefined,
        orderEmail: email.trim(), // also send as orderEmail for guest flows
        email: email.trim(),      // keep original key for backward compat
        postalCode: postal || undefined,
        // contact & issue
        name: name.trim(),
        phone: normalizePhone(phone) || undefined,
        reason,                        // enum value (e.g., 'wrong_item')
        details: details.trim() || undefined,
      }).unwrap();
      toast.success('Return request submitted');
      if (userInfo) {
        navigate('/profile');
      } else {
        setSubmitted(true);
      }
    } catch (err) {
      toast.error(err?.data?.message || err?.message || 'Failed to submit');
    }
  };

  return (
    <>
    <BackButton />
    <Row className="justify-content-center mt-3">
      <Col md={8} lg={7} xl={6}>
        <Card>
          <Card.Header><strong>Start a Return</strong></Card.Header>
          <Card.Body>
            {submitted && (
              <Alert variant="success" className="mb-3">
                Your return request has been submitted. Our team will review it and contact you by email.
              </Alert>
            )}
            {eomOrderNotReady && (
              <Alert variant="info" className="mb-3">
                This EOM order is eligible for return after delivery or pickup is completed.
              </Alert>
            )}
            {!userInfo ? (
              <Alert
                variant="light"
                className="mb-3 border border-primary rounded-3 shadow-sm"
                style={{ background: '#e8f1ff', fontSize: '1rem', lineHeight: 1.5 }}
              >
                <p className="mb-1">
                  <strong className="text-primary">Have an account?</strong>{' '}
                  Please{' '}
                  <Link to="/login?redirect=/return-request" className="fw-semibold text-primary">
                    sign in
                  </Link>{' '}
                  and go to <strong>Profile → Orders → Return Request</strong> for faster processing.
                </p>
                <p className="mb-0">
                  <strong className="text-success">No account?</strong>{' '}
                  Submit a <strong className="text-success">guest return request</strong> below.
                </p>
              </Alert>
            ) : (
              <Alert
                variant="success"
                className="mb-3 border border-success rounded-3 shadow-sm"
                style={{ background: '#e9f7ef', fontSize: '1rem', lineHeight: 1.5 }}
              >
                <p className="mb-0">
                  You’re signed in as <strong className="fw-semibold text-success">{userInfo.name}</strong>. For the fastest
                  experience, open{' '}
                  <Link to="/profile" className="fw-semibold text-success">Profile → My Orders</Link>{' '}
                  and start the return there. If this order was placed as a <em>guest under a different email</em>, use the form below.
                </p>
              </Alert>
            )}
            <Form onSubmit={submit}>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Label>Your name</Form.Label>
                  <Form.Control value={name} onChange={(e) => setName(e.target.value)} />
                </Col>
                <Col md={6}>
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={!!userInfo && !!orderId}
                  />
                </Col>

                 {/* Guest order match fields */}
                <Col md={6}>
                  <Form.Label>Order / Invoice Number</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g., INV-2025-00123 or 10004567"
                    value={orderNumber}
                    onChange={(e) => setOrderNumber(e.target.value)}
                  />
                  <Form.Text className="text-muted">
                    If you checked out as a guest, enter the number from your invoice email/PDF.
                  </Form.Text>
                </Col>
                <Col md={6}>
                  <Form.Label>Postal/ZIP (optional)</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="K1A0B1"
                    value={postalCode}
                    onChange={(e) => setPostalCode(e.target.value)}
                  />
                </Col>

                <Col md={6}>
                  <Form.Label>Phone (optional)</Form.Label>
                  <Form.Control value={phone} onChange={(e) => setPhone(e.target.value)} />
                </Col>

                <Col md={12}>
                  <Form.Label>Reason</Form.Label>
                  <Form.Select value={reason} onChange={(e) => setReason(e.target.value)}>
                    {REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Most returns are accepted within 30 days of delivery or pickup. For EOM accounts, approved returns may be applied as a credit to monthly billing.
                  </Form.Text>
                </Col>

                <Col md={12}>
                  <Form.Label>Details (optional)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={details}
                    onChange={(e) => setDetails(e.target.value)}
                    placeholder="Describe the issue…"
                  />
                </Col>

                <Col xs="auto">
                  <Button type="submit" disabled={!canSubmit || isLoading}>
                    {isLoading ? 'Submitting…' : 'Submit Return Request'}
                  </Button>
                </Col>
                <Col xs="auto">
                  <Button variant="outline-secondary" onClick={() => navigate('/returns')}>
                    Cancel
                  </Button>
                </Col>
              </Row>
            </Form>
          </Card.Body>
        </Card>
        {isLoading && <Loader />}
      </Col>
    </Row>
    </>
  );
}
